(() => {
  if (window.icafeMotionV16Ready) return;
  window.icafeMotionV16Ready = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const narrow = matchMedia('(max-width: 767px)');
  const seen = new WeakSet();
  const pending = new Set();
  const running = new Map();
  const roots = new Set();
  const queued = new Set();
  const pressedPointers = new Set();
  const held = new Set();
  let revealObserver;
  let mutationObserver;
  let scanFrame = 0;
  const enabled = () => document.body?.dataset.icafeMotion === 'true' && !reduced.matches && !navigator.connection?.saveData;
  const repeated = '.ic-atelier,.ic-catalog-card,.hdt-card-product,.ic-collection-tile,.ic-cross-card,.ic-quick-cross,.ic-service-links>a,.ic-story-links>a,.ic-roast-table__grid>a,.ic-journal__roast-grid>a,.ic-journal-grid>article,.ic-feature-blends>div,.icc-ingredients__grid>article,.hdt-page-cart__item,.hdt-mini-cart__item,.ic-drawer-coffee,.ic-footer-v11__links,.ic-faq details,.icc-product-faq details,.ic-collection-nav>a,#hdt-nav-ul-mb>li';
  const sectionItems = '.ic-atelier,.ic-catalog-card,.hdt-card-product,.ic-collection-tile,.ic-cross-card,.ic-quick-cross,.ic-service-links>a,.ic-story-links>a,.ic-roast-table__grid>a,.ic-journal__roast-grid>a,.ic-journal-grid>article,.ic-feature-blends>div,.icc-ingredients__grid>article,.hdt-page-cart__item,.hdt-mini-cart__item,.ic-drawer-coffee,.ic-collection-nav>a,.hdt-reval-item';
  const sectionSurfaces = `${sectionItems},.ic-products,.ic-discovery__track,.ic-collection-grid,hdt-reval-items`;
  const grouped = '.ic-hero__copy,.ic-turkish-banner__copy,.ic-collection-hero__copy,.ic-section-head,.ic-journal__hero>div,.ic-journal__next>div,.ic-page__head,.ic-contact>div,.ic-contact__form,.ic-quick-pair__layout>header,.icc-ingredients__head,.ic-faq>div:first-child,.ic-footer-v11__brand,.ic-footer-v11__newsletter,.ic-footer-v11__confidence,.ic-footer-v11__bottom,.hdt-main-cart__footer,.hdt-main-product-quick-view .hdt-group-product-block';
  const content = 'h1,h2,h3,h4,p,blockquote,dl,.ic-prose,.ic-eyebrow,.ic-hero__actions,.ic-feature-layout__copy>.ic-text-link,.ic-product-profile,.ic-product-choices,.ic-confidence,.icc-overview,.hdt-product-info__list>.hdt-product-info__item,.hdt-product-info__list>[id],.shopify-policy__body>*,.hdt-rte>ul,.hdt-rte>ol,.ic-journal__body>ul,.ic-journal__body>ol,form.contact,form#ContactForm';
  const media = '.ic-feature-layout__media,.ic-brewing__image,.ic-journal__image,.ic-journal__section img,.ic-specific-details>img,.hdt-product-media__main,.hdt-main-product-quick-view .hdt-product-media-wrapps';
  const ignore = '[hidden],[inert],[aria-hidden="true"],.sr-only,template,script,style,[data-ic-motion-skip],.hdt-sticky-atc,hdt-sticky-btn-atc,.hdt-sticky-btn-atc,dialog:not([open]),.ic-header-v12';
  const allowed = element => !!element.closest('#MainContent,icafe-site-footer,.ic-footer-v11,dialog[open]');
  const hidden = element => {
    if (element.closest(ignore)) return true;
    const closed = element.closest('details:not([open])');
    return !!closed && closed !== element && !element.closest('summary');
  };
  const all = (scope, selector) => [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];
  const stop = element => {
    held.delete(element);
    if (pending.delete(element)) revealObserver?.unobserve(element);
    const animation = running.get(element);
    if (animation) { animation.cancel(); running.delete(element); }
    if (element.isConnected && element.dataset.icMotionNode) element.dataset.icMotionState = 'done';
  };
  const reveal = element => {
    revealObserver?.unobserve(element);
    pending.delete(element);
    if (!enabled() || hidden(element) || !element.isConnected) { stop(element); return; }
    const rect = element.getBoundingClientRect();
    if (rect.bottom <= 0 || rect.top >= innerHeight + 100 || rect.width <= 0 || rect.height <= 0) { stop(element); return; }
    if (running.size >= 28 || !element.animate) { element.dataset.icMotionState = 'done'; return; }
    const initial = element.dataset.icMotionInitial === 'true';
    const rise = element.dataset.icMotionMode === 'section';
    const stagger = rise || element.dataset.icMotionMode === 'stagger';
    const base = rise ? 'none' : getComputedStyle(element).transform;
    const rest = base && base !== 'none' ? base : '';
    const distance = narrow.matches ? 28 : 44;
    const direction = element.dataset.icMotionDirection === 'left' ? -1 : 1;
    const transform = rise ? `translate3d(0,${narrow.matches ? 24 : 32}px,0)` : stagger ? `translate3d(0,${narrow.matches ? 28 : 36}px,0) scale(.965)` : `translate3d(${distance * direction}px,0,0)`;
    const animation = element.animate([
      { opacity: rise ? 0 : initial ? 1 : .06, transform: `${transform} ${rest}`.trim() },
      { opacity: 1, transform: rest || 'translate3d(0,0,0)' }
    ], { duration: rise ? 720 : stagger ? 820 : initial ? 780 : 940, delay: stagger ? Number(element.dataset.icMotionDelay || 0) : 0, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'backwards' });
    running.set(element, animation);
    element.dataset.icMotionState = 'animating';
    const done = () => {
      if (running.get(element) === animation) running.delete(element);
      if (element.isConnected) element.dataset.icMotionState = 'done';
    };
    animation.finished.then(done, done);
  };
  const register = (element, mode, isMedia = false) => {
    if (seen.has(element) || !allowed(element) || hidden(element)) return;
    if (element.matches(sectionItems) || (mode !== 'section' && element.closest(sectionSurfaces))) return;
    if (element.parentElement?.closest('[data-ic-motion-node]') || element.querySelector('[data-ic-motion-node]')) return;
    if (mode !== 'stagger' && mode !== 'section' && element.closest(repeated)) return;
    const style = getComputedStyle(element);
    if (style.position === 'fixed' || style.position === 'sticky' || style.display === 'none') return;
    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) return;
    const initial = rect.top < innerHeight && rect.bottom > 0;
    const center = rect.left + rect.width / 2;
    const centered = Math.abs(center - innerWidth / 2) < innerWidth * .12;
    const fromLeft = centered ? isMedia || document.documentElement.dir === 'ltr' : center < innerWidth / 2;
    seen.add(element);
    element.dataset.icMotionNode = 'true';
    element.dataset.icMotionMode = mode;
    element.dataset.icMotionDirection = fromLeft ? 'left' : 'right';
    element.dataset.icMotionInitial = String(initial);
    element.dataset.icMotionMedia = String(isMedia);
    let index = 0;
    if (mode === 'stagger' && element.parentElement) {
      const row = [...element.parentElement.children].filter(peer => {
        if (!peer.matches(repeated) || hidden(peer)) return false;
        const peerRect = peer.getBoundingClientRect();
        return peerRect.width > 0 && Math.abs(peerRect.top - rect.top) < 8;
      });
      index = Math.max(0, row.indexOf(element));
    }
    element.dataset.icMotionDelay = String(Math.min(index * 140, 420));
    if (rect.bottom <= 0 || initial) { element.dataset.icMotionState = 'done'; return; }
    element.dataset.icMotionState = 'observed';
    pending.add(element);
    revealObserver.observe(element);
  };
  const enhance = scope => {
    if (!enabled() || !scope?.querySelectorAll) return;
    if (scope.closest?.('[data-ic-motion-mode=section]')) {
      all(scope, '[hdt-reveal]').forEach(node => node.removeAttribute('hdt-reveal'));
      return;
    }
    const sections = [...new Set(all(scope, sectionSurfaces).filter(element => !hidden(element)).map(element => element.closest('section,.shopify-section')).filter(element => element && allowed(element) && !element.matches('.ic-hero') && !element.querySelector('.ic-hero')))];
    sections.filter(element => !sections.some(parent => parent !== element && parent.contains(element))).forEach(element => {
      all(element, '[hdt-reveal]').forEach(node => node.removeAttribute('hdt-reveal'));
      if (seen.has(element)) return;
      cleanup(element);
      register(element, 'section');
    });
    all(scope, media).forEach(element => register(element, 'slide', true));
    all(scope, grouped).forEach(element => register(element, 'slide'));
    all(scope, repeated).forEach(element => register(element, 'stagger'));
    all(scope, content).forEach(element => register(element, 'slide'));
  };
  const queueScan = scope => {
    if (!enabled() || !scope?.querySelectorAll) return;
    queued.add(scope);
    if (scanFrame) return;
    scanFrame = requestAnimationFrame(() => {
      scanFrame = 0;
      const scopes = [...queued];
      queued.clear();
      scopes.filter(scope => !scopes.some(other => other !== scope && other.contains(scope))).forEach(enhance);
    });
  };
  const cleanup = scope => {
    if (!scope?.querySelectorAll) return;
    all(scope, '[data-ic-motion-node]').forEach(element => {
      stop(element);
      seen.delete(element);
      delete element.dataset.icMotionNode;
    });
  };
  const cancelAll = () => {
    cancelAnimationFrame(scanFrame);
    scanFrame = 0;
    queued.clear();
    pressedPointers.clear();
    held.clear();
    revealObserver?.disconnect();
    pending.forEach(element => { seen.delete(element); delete element.dataset.icMotionNode; element.dataset.icMotionState = 'done'; });
    pending.clear();
    [...running.keys()].forEach(stop);
    if (document.body) document.body.dataset.icMotionStatus = 'off';
  };
  const watch = root => {
    if (!root) return;
    if (!roots.has(root) && ![...roots].some(parent => parent.contains(root))) {
      roots.add(root);
      mutationObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'open'] });
    }
    enhance(root);
    queueScan(root);
  };
  const trackHeroIntro = () => {
    document.querySelectorAll('.ic-hero__image,.ic-hero__copy>*').forEach(element => {
      if (running.has(element)) return;
      const animation = element.getAnimations?.().find(item => item.animationName?.startsWith('icafe-v16-hero-') && item.playState !== 'finished' && item.playState !== 'idle');
      if (!animation) return;
      running.set(element, animation);
      const done = () => { if (running.get(element) === animation) running.delete(element); };
      animation.finished.then(done, done);
    });
  };
  const init = () => {
    if (!document.body) return;
    trackHeroIntro();
    if (!('IntersectionObserver' in window)) { document.body.dataset.icMotionStatus = 'off'; return; }
    revealObserver ||= new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) reveal(entry.target); });
    }, { threshold: .02, rootMargin: '0px 0px -3% 0px' });
    mutationObserver ||= new MutationObserver(records => {
      records.forEach(record => {
        if (record.type === 'attributes') {
          if (record.attributeName === 'data-icafe-motion') { if (enabled()) init(); else cancelAll(); }
          else if (!record.target.hidden) queueScan(record.target);
          return;
        }
        record.removedNodes.forEach(cleanup);
        record.addedNodes.forEach(queueScan);
      });
    });
    mutationObserver.observe(document.body, { attributes: true, attributeFilter: ['data-icafe-motion'] });
    if (!enabled()) { cancelAll(); return; }
    document.body.dataset.icMotionStatus = 'on';
    watch(document.getElementById('MainContent'));
    document.querySelectorAll('icafe-site-footer,.ic-footer-v11,dialog[open]').forEach(watch);
    roots.forEach(enhance);
  };
  const finishAtTarget = event => {
    if (!(event.target instanceof Element)) return;
    if (event.type === 'focusin' && pressedPointers.size) return;
    let current = event.target;
    while (current && current !== document.body) { stop(current); current = current.parentElement; }
  };
  const holdAtTarget = event => {
    if (!(event.target instanceof Element)) return;
    pressedPointers.add(event.pointerId);
    let current = event.target;
    while (current && current !== document.body) {
      const animation = running.get(current);
      if (animation) { animation.pause(); held.add(current); }
      else if (pending.has(current)) stop(current);
      current = current.parentElement;
    }
  };
  const releasePointer = event => {
    pressedPointers.delete(event.pointerId);
    if (!held.size) return;
    requestAnimationFrame(() => {
      if (pressedPointers.size) return;
      [...held].forEach(stop);
    });
  };
  const dialogEnter = event => {
    if (!enabled()) return;
    const dialog = event.target.matches?.('dialog') ? event.target : event.target.querySelector?.('dialog[open]');
    if (dialog?.open) watch(dialog);
  };
  addEventListener('pagehide', cancelAll);
  addEventListener('pageshow', init);
  addEventListener('resize', () => roots.forEach(queueScan), { passive: true });
  document.addEventListener('focusin', finishAtTarget, true);
  document.addEventListener('pointerdown', holdAtTarget, { capture: true, passive: true });
  document.addEventListener('pointerup', releasePointer, { capture: true, passive: true });
  document.addEventListener('pointercancel', releasePointer, { capture: true, passive: true });
  document.addEventListener('click', finishAtTarget, true);
  document.addEventListener('dialog:open', dialogEnter, true);
  document.addEventListener('shopify:section:load', event => { trackHeroIntro(); watch(event.target); });
  document.addEventListener('shopify:section:unload', event => { cleanup(event.target); roots.delete(event.target); });
  reduced.addEventListener('change', () => { if (enabled()) init(); else cancelAll(); });
  navigator.connection?.addEventListener?.('change', () => { if (enabled()) init(); else cancelAll(); });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
